import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { FC, useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "../lib/toast";
import { apiClient } from "../utils/api";
import ConnectionStatus from "../components/ConnectionStatus";
import { useAuth } from "../contexts/AuthContext";

interface AuthStatus {
  hubspot: "pending" | "loading" | "completed" | "error";
  veracross: "pending" | "loading" | "completed" | "error";
}

const AccountAuthorization: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { encryptedUserId } = useAuth();
  const [authStatus, setAuthStatus] = useState<AuthStatus>({
    hubspot: "pending",
    veracross: "pending",
  });
  const [hubspotAuthUrl, setHubspotAuthUrl] = useState<string | null>(null);
  const hasFetchedAuthUrl = useRef(false);

  useEffect(() => {
    const veracrossCompleted = searchParams.get("veracrossCompleted");

    if (encryptedUserId) {
      checkAuthenticationStatus(encryptedUserId);
    }

    if (veracrossCompleted === "true") {
      setAuthStatus((prev) => ({ ...prev, veracross: "completed" }));
    }
  }, [searchParams, encryptedUserId]);

  useEffect(() => {
    getHubSpotAuthUrl();
    
    return () => {
      // Cancel any pending requests when component unmounts
      apiClient.cancelAllRequests();
    };
  }, []);

  const getHubSpotAuthUrl = async () => {
    // Prevent duplicate API calls
    if (hasFetchedAuthUrl.current) {
      return;
    }
    hasFetchedAuthUrl.current = true;

    try {
      const response = await apiClient.getHubSpotAuthUrl();
      if (response.success) {
        setHubspotAuthUrl(response.authUrl);
      }
    } catch (error) {
      // Error getting HubSpot auth URL - will be handled by retry logic
    }
  };

  const checkAuthenticationStatus = async (userId: string) => {
    try {
      const onboardingResponse = await apiClient.checkUserOnboardingStatus(userId);
      
      if (onboardingResponse.success) {
        localStorage.setItem("encryptedUserId", userId);
        
        setAuthStatus({
          hubspot: onboardingResponse.hasHubSpotToken ? "completed" : "pending",
          veracross: onboardingResponse.hasVeracrossToken ? "completed" : "pending",
        });
        
        if (onboardingResponse.hasBothTokens) {
          const nextUrl = `/applicant-field-mapping?userId=${encodeURIComponent(userId)}`;
          navigate(nextUrl);
          return;
        }
      } else {
        setAuthStatus({
          hubspot: "pending",
          veracross: "pending",
        });
        localStorage.removeItem("encryptedUserId");
      }
    } catch (error) {
      setAuthStatus({
        hubspot: "pending",
        veracross: "pending",
      });
      localStorage.removeItem("encryptedUserId");
    }
  };

  const handleHubSpotAuth = () => {
    if (hubspotAuthUrl) {
      setAuthStatus((prev) => ({ ...prev, hubspot: "loading" }));
      window.location.href = hubspotAuthUrl;
    } else {
      toast({
        title: "Error",
        description: "HubSpot authorization URL not available",
        variant: "destructive",
      });
    }
  };

  const handleVeracrossAuth = () => {
    setAuthStatus((prev) => ({ ...prev, veracross: "loading" }));
    const veracrossUrl = encryptedUserId
      ? `/veracross-credentials?userId=${encodeURIComponent(encryptedUserId)}`
      : "/veracross-credentials";
    navigate(veracrossUrl);
  };

  const handleContinue = () => {
    if (
      authStatus.hubspot === "completed" &&
      authStatus.veracross === "completed"
    ) {
      const nextUrl = encryptedUserId
        ? `/applicant-field-mapping?userId=${encodeURIComponent(encryptedUserId)}`
        : "/applicant-field-mapping";
      navigate(nextUrl);
    } else {
      toast({
        title: "Authentication Required",
        description:
          "Please complete both HubSpot and Veracross authentication before continuing",
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (
    status: "pending" | "loading" | "completed" | "error"
  ) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "loading":
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return (
          <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
        );
    }
  };

  const getStatusText = (
    status: "pending" | "loading" | "completed" | "error"
  ) => {
    switch (status) {
      case "completed":
        return "Authenticated";
      case "loading":
        return "Authenticating...";
      case "error":
        return "Authentication Failed";
      default:
        return "Not Authenticated";
    }
  };

  const isBothAuthenticated =
    authStatus.hubspot === "completed" && authStatus.veracross === "completed";

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">
            Account Authorization
          </CardTitle>
          <CardDescription>
            Connect your HubSpot and Veracross accounts to enable data
            synchronization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ConnectionStatus />
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-[#ff7a59] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">H</span>
                </div>
                <div>
                  <h3 className="font-semibold">HubSpot CRM</h3>
                  <p className="text-sm text-gray-600">
                    Connect your HubSpot account
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusIcon(authStatus.hubspot)}
                <span
                  className={`text-sm font-medium ${
                    authStatus.hubspot === "completed"
                      ? "text-green-600"
                      : authStatus.hubspot === "loading"
                        ? "text-blue-600"
                        : authStatus.hubspot === "error"
                          ? "text-red-600"
                          : "text-gray-600"
                  }`}
                >
                  {getStatusText(authStatus.hubspot)}
                </span>
              </div>
            </div>
            {authStatus.hubspot !== "completed" && (
              <>
                <Button
                  onClick={handleHubSpotAuth}
                  disabled={authStatus.hubspot === "loading" || !hubspotAuthUrl}
                  className="w-full bg-[#ff7a59] hover:bg-[#e66a4a] text-white"
                >
                  {authStatus.hubspot === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Authorizing...
                    </>
                  ) : (
                    "Authorize HubSpot"
                  )}
                </Button>
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Connect your HubSpot account to enable CRM data synchronization
                </p>
              </>
            )}
          </div>

          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">V</span>
                </div>
                <div>
                  <h3 className="font-semibold">Veracross</h3>
                  <p className="text-sm text-gray-600">
                    Connect your Veracross account
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusIcon(authStatus.veracross)}
                <span
                  className={`text-sm font-medium ${
                    authStatus.veracross === "completed"
                      ? "text-green-600"
                      : authStatus.veracross === "loading"
                        ? "text-blue-600"
                        : authStatus.veracross === "error"
                          ? "text-red-600"
                          : "text-gray-600"
                  }`}
                >
                  {getStatusText(authStatus.veracross)}
                </span>
              </div>
            </div>
            {authStatus.veracross !== "completed" && (
              <>
                <Button
                  onClick={handleVeracrossAuth}
                  disabled={authStatus.veracross === "loading"}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {authStatus.veracross === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Authorizing...
                    </>
                  ) : (
                    "Authorize Veracross"
                  )}
                </Button>
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Connect your Veracross account to enable student data synchronization
                </p>
              </>
            )}
          </div>

          <div className="pt-4 border-t">
            <Button
              onClick={handleContinue}
              disabled={!isBothAuthenticated}
              className="w-full"
              size="lg"
            >
              Continue to Field Mapping
            </Button>
            {!isBothAuthenticated && (
              <p className="text-sm text-gray-600 text-center mt-2">
                Both HubSpot and Veracross must be authenticated to continue
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountAuthorization;
